import React, { useState, useRef, useCallback, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronUp, faChevronDown, faSpinner } from '@fortawesome/free-solid-svg-icons';
import AccountSummary from '../components/AccountSummary';
import PositionsTable from '../components/PositionsTable';
import PositionsHeatmap from '../components/PositionsHeatmap';
import IndexBenchmarks from '../components/IndexBenchmarks';
import OrderForm from '../components/OrderForm';
import MultiLegOrderForm from '../components/MultiLegOrderForm';
import MultiStockOrderForm from '../components/MultiStockOrderForm';
import OpenOrdersTable from '../components/OpenOrdersTable';
import WatchlistAlpaca from '../components/WatchlistAlpaca';
import NewsFeed from '../components/NewsFeed';
import MostActiveStocks from '../components/MostActiveStocks';
import MarketMovers from '../components/MarketMovers';
import StockScanner from '../components/StockScanner';
import AssetSearch from '../components/AssetSearch';
import UpcomingCorporateEvents from '../components/UpcomingCorporateEvents';
import EarningsCalendar from '../components/EarningsCalendar';
import EconomicCalendar from '../components/EconomicCalendar';
import RelativeStrength from '../components/RelativeStrength';
import OptionsOverview from '../components/OptionsOverview';
import MarketClock from '../components/MarketClock';
import { LiveStreamCard } from '../components/LiveStream';
import { accountApi } from '../api/accountApi';
import { useAuth } from '../contexts/AuthContext';
import { useQuote } from '../contexts/QuoteContext';

const Dashboard = () => {
  const { maskAmounts, alpacaOnly } = useAuth();
  const { symbol: quoteSymbol, selectSymbol, registerOrderSubmitted } = useQuote();
  const [selectedSymbol, setSelectedSymbol] = useState('AAPL');
  const [isOrderFormExpanded, setIsOrderFormExpanded] = useState(false);
  const [orderTicketTab, setOrderTicketTab] = useState('stock'); // stock | multi | mleg
  const [combinedPortfolio, setCombinedPortfolio] = useState(null);
  const [loadingCombined, setLoadingCombined] = useState(false);
  const [balanceSummary, setBalanceSummary] = useState(null);
  const refreshAccountRef = useRef(null);
  const refreshPositionsRef = useRef(null);
  const refreshOrdersRef = useRef(null);
  const [isPaperEnv] = useState(() => (localStorage.getItem('tradingEnv') || 'paper') === 'paper');
  const [deskVenue, setDeskVenue] = useState(
    () => localStorage.getItem('dashboardVenue') || 'alpaca'
  );
  const [schwabAccount, setSchwabAccount] = useState(
    () => localStorage.getItem('dashboardSchwabAccount') || ''
  );

  const changeDeskVenue = (next) => {
    setDeskVenue(next);
    localStorage.setItem('dashboardVenue', next);
  };

  const changeSchwabAccount = (next) => {
    setSchwabAccount(next);
    localStorage.setItem('dashboardSchwabAccount', next);
  };

  const loadCombinedPortfolio = async () => {
    try {
      setLoadingCombined(true);
      const [combinedRes, summaryRes] = await Promise.all([
        accountApi.getCombinedPortfolio(),
        accountApi.getBalanceSummary().catch(() => null),
      ]);
      setCombinedPortfolio(combinedRes.data);
      setBalanceSummary(summaryRes?.data || null);
    } catch (error) {
      console.error('Error loading combined portfolio:', error);
    } finally {
      setLoadingCombined(false);
    }
  };

  useEffect(() => {
    if (isPaperEnv || alpacaOnly) {
      return;
    }
    loadCombinedPortfolio();
    const interval = setInterval(loadCombinedPortfolio, 30000);
    return () => clearInterval(interval);
  }, [isPaperEnv, alpacaOnly]);

  useEffect(() => {
    if (quoteSymbol) {
      setSelectedSymbol(quoteSymbol);
    }
  }, [quoteSymbol]);

  const handleOrderSubmitted = useCallback(() => {
    if (refreshAccountRef.current) refreshAccountRef.current.refresh();
    if (refreshPositionsRef.current) refreshPositionsRef.current.refresh();
    if (refreshOrdersRef.current) refreshOrdersRef.current.refresh();
    if (!alpacaOnly && !isPaperEnv) {
      loadCombinedPortfolio();
    }
  }, [alpacaOnly, isPaperEnv]);

  useEffect(() => registerOrderSubmitted(handleOrderSubmitted), [registerOrderSubmitted, handleOrderSubmitted]);

  const formatAmount = (value) => {
    const num = value || 0;
    return maskAmounts
      ? '***'
      : num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const renderDelta = (change, pct) => {
    if (change === null || change === undefined) return null;
    const positive = change >= 0;
    return (
      <span className={`num text-[11px] font-medium ${positive ? 'text-bull' : 'text-bear'}`}>
        {positive ? '+' : ''}
        {change.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        {pct !== null && pct !== undefined
          ? ` (${positive ? '+' : ''}${pct.toFixed(2)}%)`
          : ''}
      </span>
    );
  };

  const todayGainLossValue =
    combinedPortfolio?.combined?.todayGainLoss ??
    balanceSummary?.change ??
    null;

  const todayGainLossPercent =
    combinedPortfolio?.combined?.todayGainLossPercent ??
    balanceSummary?.changePct ??
    null;

  return (
    <>
      <main className="flex-1 mx-auto w-full max-w-[1600px] px-4 lg:px-6 py-4 pb-28">
        <div className="md:hidden mb-3">
          <AssetSearch />
        </div>

        {!alpacaOnly && !isPaperEnv && combinedPortfolio ? (
          <div className="lt-card p-4 mb-3">
            <div className="flex items-end justify-between gap-4 mb-4">
              <div>
                <div className="lt-label mb-1">Total portfolio value</div>
                <div className="num text-[32px] font-semibold tracking-tight text-ink leading-none">
                  ${formatAmount(combinedPortfolio.combined.totalValue || 0)}
                </div>
                <div className="mt-2 flex items-center gap-3">
                  {renderDelta(maskAmounts ? 0 : todayGainLossValue, todayGainLossPercent)}
                  <span className="text-[11px] text-muted">24h</span>
                </div>
              </div>
              {loadingCombined && (
                <FontAwesomeIcon icon={faSpinner} className="animate-spin text-muted" />
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
              {[
                ['Alpaca equity', combinedPortfolio.alpaca.equity || combinedPortfolio.alpaca.portfolioValue || 0],
                ['Alpaca cash', combinedPortfolio.alpaca.cash || 0],
                ['Portfolio value', combinedPortfolio.customPortfolios.totalValue || 0],
                ['Portfolio cash', combinedPortfolio.customPortfolios.totalCash || 0],
                ['Total cash', combinedPortfolio.combined.totalCash || 0],
                ['Cost basis', combinedPortfolio.combined.totalCost || 0],
              ].map(([label, value]) => (
                <div key={label} className="rounded-md border border-hairline bg-canvas p-3">
                  <div className="lt-label mb-1">{label}</div>
                  <div className="num text-[15px] font-medium text-ink">${formatAmount(value)}</div>
                </div>
              ))}
            </div>
          </div>
        ) : !alpacaOnly && !isPaperEnv ? (
          <div className="lt-card p-4 mb-3">
            <div className="lt-label mb-2">Total portfolio value</div>
            <div className="text-sm text-muted">Loading portfolio data...</div>
          </div>
        ) : null}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
          <div className="lg:col-span-3 space-y-3">
            <AccountSummary
              ref={refreshAccountRef}
              venue={deskVenue}
              onVenueChange={changeDeskVenue}
              schwabAccount={schwabAccount}
              onSchwabAccountChange={changeSchwabAccount}
            />
            <LiveStreamCard />
            <PositionsHeatmap
              onSymbolSelect={selectSymbol}
              venue={deskVenue}
              schwabAccount={schwabAccount}
            />
            <PositionsTable
              ref={refreshPositionsRef}
              onOrderExecuted={handleOrderSubmitted}
              onSymbolSelect={selectSymbol}
              venue={deskVenue}
              schwabAccount={schwabAccount}
            />
            <OpenOrdersTable
              ref={refreshOrdersRef}
              onOrderUpdated={handleOrderSubmitted}
            />
            <WatchlistAlpaca onSymbolSelect={selectSymbol} />

            <StockScanner onSymbolSelect={selectSymbol} />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <MostActiveStocks onSymbolSelect={selectSymbol} />
              <MarketMovers direction="gainers" onSymbolSelect={selectSymbol} />
              <MarketMovers direction="losers" onSymbolSelect={selectSymbol} />
            </div>
          </div>

          <div className="lg:col-span-1 space-y-3">
            <MarketClock />
            <IndexBenchmarks />
            <RelativeStrength onSymbolSelect={selectSymbol} />
            <OptionsOverview onSymbolSelect={selectSymbol} />
            <EarningsCalendar onSymbolSelect={selectSymbol} />
            <EconomicCalendar />
            <UpcomingCorporateEvents />
            <NewsFeed />
          </div>
        </div>
      </main>

      <div className="fixed bottom-0 left-0 right-0 z-50 border-t border-hairline bg-surface/95 backdrop-blur-md">
        <div className="mx-auto max-w-[1600px] px-4 lg:px-6">
          <div className="w-full flex items-center justify-between min-h-[44px] py-2 gap-3">
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted">Order ticket</span>
              <div className="inline-flex rounded-md border border-hairline overflow-hidden text-[11px]">
                <button
                  type="button"
                  onClick={() => {
                    setOrderTicketTab('stock');
                    setIsOrderFormExpanded(true);
                  }}
                  className={`px-2.5 py-1 ${
                    orderTicketTab === 'stock'
                      ? 'bg-[var(--lt-action)] text-white'
                      : 'text-muted hover:bg-elevated'
                  }`}
                >
                  Stock
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setOrderTicketTab('multi');
                    setIsOrderFormExpanded(true);
                  }}
                  className={`px-2.5 py-1 ${
                    orderTicketTab === 'multi'
                      ? 'bg-[var(--lt-action)] text-white'
                      : 'text-muted hover:bg-elevated'
                  }`}
                >
                  Multi-stock
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setOrderTicketTab('mleg');
                    setIsOrderFormExpanded(true);
                  }}
                  className={`px-2.5 py-1 ${
                    orderTicketTab === 'mleg'
                      ? 'bg-[var(--lt-action)] text-white'
                      : 'text-muted hover:bg-elevated'
                  }`}
                >
                  Multi-leg
                </button>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setIsOrderFormExpanded(!isOrderFormExpanded)}
              className="hover:bg-elevated/60 transition-colors px-2 py-1 rounded"
            >
              <FontAwesomeIcon
                icon={isOrderFormExpanded ? faChevronDown : faChevronUp}
                className="text-xs text-muted"
              />
            </button>
          </div>

          <div
            className={`overflow-hidden transition-all duration-300 ease-in-out ${
              isOrderFormExpanded
                ? orderTicketTab === 'mleg' || orderTicketTab === 'multi'
                  ? 'max-h-[720px] opacity-100'
                  : 'max-h-[560px] opacity-100'
                : 'max-h-0 opacity-0'
            }`}
          >
            <div className="pb-3">
              {orderTicketTab === 'mleg' ? (
                <MultiLegOrderForm
                  symbol={selectedSymbol}
                  onOrderSubmitted={handleOrderSubmitted}
                />
              ) : orderTicketTab === 'multi' ? (
                <MultiStockOrderForm
                  symbol={selectedSymbol}
                  onOrderSubmitted={handleOrderSubmitted}
                />
              ) : (
                <OrderForm
                  symbol={selectedSymbol}
                  onOrderSubmitted={handleOrderSubmitted}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Dashboard;
